import { ValidationError } from "@/errors";
import { hasActivePendingJobs } from "@/lib/jobGenerationState";
import { useStore } from "@/store";
import { useCallback, useEffect, useState } from "react";
import { useJobs } from "./useJobs";
import { useSonnerToast } from "./useSonnerToast";
import { useUrlValidation } from "./useUrlValidation";

/**
 * Custom hook that centralizes URL validation, context setting (domain/page),
 * and analysis job initiation logic. Replaces useWorkspaceAnalysis.
 */
export const useAnalysis = () => {
  // Get required hooks and store actions
  const ensureDomainAndPageExists = useStore(
    (state) => state.ensureDomainAndPageExists,
  ); // Get directly from store
  const { analyzeUrl, isLoadingJobs, jobs } = useJobs();
  const { validateUrl } = useUrlValidation();
  const { toast } = useSonnerToast();

  // Local state for tracking analysis status
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Computed generation state based on pending jobs. hasActivePendingJobs
  // already excludes stale (timed-out) jobs purely by comparing created_at
  // client-side -- no Firestore write is needed to "mark" a job stale, so
  // there's no separate cleanup step to run (heuristic_jobs is
  // backend-write-only per firestore.rules; the client can only read it).
  const isGeneratingAnalysis =
    jobs && jobs.length >= 0 ? hasActivePendingJobs(jobs) : false;

  // Effect to reset analyzing state when job loading state changes
  useEffect(() => {
    if (!isLoadingJobs && isAnalyzing) {
      setIsAnalyzing(false);
    }
  }, [isLoadingJobs, isAnalyzing]);

  /**
   * Ensures the domain and page context exists for a given URL and sets it as current.
   * Does not start an analysis job.
   * 1. Validates the URL.
   * 2. Calls ensureDomainAndPageExists from the store.
   *
   * @param url - The URL to set context for.
   * @returns Promise resolving to the page ID if successful, otherwise null.
   */
  const ensureUrlContext = useCallback(
    async (url: string): Promise<string | null> => {
      // Validate URL
      const validation = validateUrl(url);
      if (!validation.isValid) {
        if (url.trim()) {
          toast({
            variant: "destructive",
            title: "Invalid URL",
            description: validation.errorMessage,
          });
        }
        return null;
      }

      try {
        // Ensure domain/page exist and set as current
        const context = await ensureDomainAndPageExists(validation.validUrl!);
        return context?.pageId ?? null; // Return pageId on success
      } catch (error) {
        console.error("Error ensuring URL context:", error);
        toast({
          variant: "destructive",
          title: "Error Setting Context",
          description:
            "An error occurred while setting up the domain and page for this URL.",
        });
        return null;
      }
    },
    [validateUrl, toast, ensureDomainAndPageExists],
  );

  /**
   * Starts an analysis job for the given URL.
   * Assumes the context (domain/page) for the URL might need to be set first.
   * 1. Validates the URL.
   * 2. Ensures the domain/page context exists and is set as current.
   * 3. Starts the analysis job via startJob.
   *
   * @param url - The URL to analyze.
   * @returns Promise resolving to the job ID if successful, otherwise null.
   */
  const startAnalysisJob = useCallback(
    async (url: string): Promise<string | null> => {
      const validation = validateUrl(url);
      if (!validation.isValid) {
        // Only show error toast if user has actually tried to submit something
        if (url.trim()) {
          toast({
            variant: "destructive",
            title: "Error",
            description: validation.errorMessage,
          });
        }
        return null; // Return null if the first validation fails.
      }

      // Check if already generating analysis
      if (isGeneratingAnalysis || isAnalyzing) {
        console.log("Analysis generation already in progress");
        return null;
      }

      // If the first validation passes, proceed.
      setIsAnalyzing(true);
      try {
        // 1. Ensure context is set (this also creates domain/page if needed)
        // We don't necessarily need the returned pageId here, but ensureDomainAndPageExists
        // handles setting the current IDs in the userDoc, which triggers store updates.
        const context = await ensureDomainAndPageExists(validation.validUrl!);
        if (!context) {
          // Error handled within ensureDomainAndPageExists via toast
          throw new ValidationError(
            { url: "Failed to set URL context" },
            "Failed to set URL context before starting job.",
          ); // Throw to prevent job start
        }

        // 2. Start the analysis job (analyzeUrl uses the *current* domain/page IDs from the store)
        const jobId = await analyzeUrl(validation.validUrl!);
        toast({
          variant: "success",
          title: "Analysis Started",
          description: "Your URL is being analyzed.",
        });
        return jobId;
      } catch (error) {
        console.error("Error starting analysis job:", error);

        if (error instanceof ValidationError) {
          // Avoid duplicate toasts if ensureUrlContext already showed one
          if (!error.message.includes("Failed to set URL context")) {
            toast({
              variant: "destructive",
              title: "Validation Error",
              description: error.message,
            });
          }
        } else if (error instanceof Error) {
          console.error("Error starting analysis job:", error);
          // Removed toast here to avoid duplications with the toast coming from the job with status "error"
          // toast({
          //   variant: "destructive",
          //   title: "Analysis Failed",
          //   description: "An error occurred while starting the analysis.",
          // });
        }
        return null;
      } finally {
        setIsAnalyzing(false);
      }
    },
    [
      validateUrl,
      analyzeUrl,
      toast,
      ensureDomainAndPageExists,
      isGeneratingAnalysis,
      isAnalyzing,
    ],
  );

  return {
    ensureUrlContext,
    startAnalysisJob,
    isAnalyzing, // Immediate loading state (job start + ensure context)
    isGeneratingAnalysis, // Status-based generation state from pending jobs
    isLoadingJobs, // Specific loading state for job operations from useJobs
  };
};
